import { z } from 'zod';
import moment from 'moment';
import { DATEPICKER_FORMAT_DEFAULT } from '../../../appConstants';
import { baseModelSchema } from '../../model/baseModel';
import { temperatureUnitSchema } from './temperatureUnit';
import { ITripFormValues } from './tripFormValues';

/**
 * Zod schema for validating trip data.
 * Extends the base model schema with trip-specific fields.
 *
 * @property {string} [description] - Optional description of the trip
 * @property {string} loadDate - Date when the trip is loaded
 * @property {string} [driver] - Name of the driver
 * @property {string} [container] - Container identifier
 * @property {number} [containerTareWeight] - Weight of empty container
 * @property {string} [registrationNumber] - Vehicle registration number
 * @property {string} [sealNumber] - Security seal number
 * @property {number} [carrierId] - ID of the carrier company
 * @property {TemperatureUnit[]} temperatureUnits - Temperature readings for the trip
 * @property {string} [fleetNumber] - Fleet identifier
 * @property {number} [truckTypeId] - ID of the truck type
 * @property {number} [totalWeight] - Total weight of the trip
 * @property {number[]} [weighedDispatchIds] - IDs of weighed dispatches
 * @property {string} [deleteReason] - Reason for deletion if applicable
 */
export const tripSchema = baseModelSchema.extend({
    description: z.string().min(1, 'Description is required'),
    loadDate: z.string(),
    driver: z.string().optional(),
    container: z.string().optional(),
    containerTareWeight: z.number().optional(),
    registrationNumber: z.string().optional(),
    sealNumber: z.string().optional(),
    carrierId: z.number().optional(),
    temperatureUnits: z.array(temperatureUnitSchema).default([]),
    fleetNumber: z.string().optional(),
    truckTypeId: z.number().optional(),
    totalWeight: z.number().optional(),
    weighedDispatchIds: z.array(z.number()).optional(),
    deleteReason: z.string().optional(),
});

/**
 * TypeScript interface for trip data.
 * Generated from the Zod schema for type safety.
 */
export type ITrip = z.infer<typeof tripSchema>;

/**
 * Creates a validated Trip object from form values.
 *
 * @param {ITripFormValues} formValues - The form data to convert
 * @returns {ITrip} A validated trip object
 * @throws {ZodError} If validation fails
 */
export const Trip = (formValues : ITripFormValues) : ITrip => {
    const carrierIdRaw = formValues.carrier?.value;
    const carrierIdParsed = carrierIdRaw !== undefined && carrierIdRaw !== null && carrierIdRaw !== '' ? Number(carrierIdRaw) : undefined;
    const carrierId = Number.isFinite(carrierIdParsed as number) ? (carrierIdParsed as number) : undefined;

    const truckTypeIdRaw = formValues.truckType?.value;
    const truckTypeIdParsed = truckTypeIdRaw !== undefined && truckTypeIdRaw !== null && truckTypeIdRaw !== '' ? Number(truckTypeIdRaw) : undefined;
    const truckTypeId = Number.isFinite(truckTypeIdParsed as number) ? (truckTypeIdParsed as number) : undefined;

    const tareRaw = (formValues as any).containerTareWeight as unknown;
    const containerTareWeight = tareRaw === undefined || tareRaw === null || tareRaw === ''
        ? undefined
        : (Number.isFinite(Number(tareRaw)) ? Number(tareRaw) : undefined);

    return tripSchema.parse({
        id: formValues.id,
        guid: formValues.guid,
        isActive: formValues.isActive,
        description: formValues.description,
        loadDate: moment(formValues.loadDate).format(DATEPICKER_FORMAT_DEFAULT),
        driver: formValues.driver,
        container: formValues.container,
        containerTareWeight,
        sealNumber: formValues.sealNumber,
        registrationNumber: formValues.registrationNumber,
        carrierId,
        temperatureUnits: formValues.temperatureUnits ?? [],
        truckTypeId,
        fleetNumber: formValues.fleetNumber,
    });
};
